/**
 * Admin tab definitions — single source of truth.
 *
 * Extracted from Admin.jsx (Phase 2 P2 #2 decomposition · 2026-06).
 * Order matters: the Setup wizard is intentionally leftmost so a brand-
 * new operator sees it first; deep-links from any panel land on the
 * matching id (`location.state.tab = "imports"` etc).
 *
 * Adding a new tab is a 3-step change:
 *   1. Add an entry here (with a stable id + Lucide icon),
 *   2. Mount the panel inside Admin.jsx's tab-branch switch,
 *   3. (Optional) Add deep-link buttons from wherever else they belong.
 * No other files care about this list.
 */
import {
  Users, Activity, Map as MapIcon, List, Cog, HeartPulse, UserCheck,
  Sparkles, Upload, Rocket, Contact, LayoutDashboard, Gauge, ShieldCheck,
  Building2, MessageSquare, CreditCard, Megaphone, Server, UserPlus, Bot,
  Presentation, ShieldAlert, Wand2, PhoneCall, PhoneMissed, MailCheck,
} from "lucide-react";

// Launch Reset is a one-shot, DESTRUCTIVE pre-launch tool. After go-live it is
// hidden by default and only ever surfaced when explicitly opted-in (e.g. a
// preview/staging build). Production never sets this flag → the tab is absent.
// Fail-safe by design: a missing/!=true env var means the Launch tab is hidden.
// Also hostname-guarded: never enabled on the live production domain even if a
// build baked the flag true (see lib/demoMode.js).
import { SHOW_LAUNCH_TOOLS, SHOW_DEMO_TOOLS } from "../../lib/demoMode";
export { SHOW_LAUNCH_TOOLS, SHOW_DEMO_TOOLS };

const BASE_TABS = [
  { id: "onboarding", label: "Setup",       icon: Sparkles },
  { id: "dashboard",  label: "Dashboard",   icon: LayoutDashboard },
  { id: "copilot",    label: "AI Copilot",  icon: Bot },
  { id: "ai-booking", label: "AI Booking",  icon: Wand2 },
  { id: "voice-tuning", label: "Voice AI",   icon: PhoneCall },
  { id: "readiness",  label: "Launch",      icon: Rocket },
  { id: "map",        label: "Map",         icon: MapIcon },
  { id: "rides",      label: "Rides",       icon: List },
  { id: "fleet",      label: "Fleet",       icon: Users },
  { id: "safety",     label: "Safety / SOS", icon: ShieldAlert },
  { id: "customers",  label: "Customers",   icon: Contact },
  { id: "approvals",  label: "Approvals",   icon: UserCheck },
  { id: "imports",    label: "Imports",     icon: Upload },
  { id: "health",     label: "Ride Health", icon: HeartPulse },
  { id: "fatigue",    label: "Fatigue",     icon: Gauge },
  { id: "compliance", label: "Compliance",  icon: ShieldCheck },
  { id: "corporate",  label: "Corporate",   icon: Building2 },
  { id: "corp-apps",  label: "Applications", icon: UserPlus },
  { id: "subscriptions", label: "Subscriptions", icon: CreditCard },
  { id: "inbox",      label: "Inbox",       icon: MessageSquare },
  { id: "confirmations", label: "Confirmations", icon: MailCheck },
  { id: "missed-calls", label: "Missed Calls", icon: PhoneMissed },
  { id: "comms",      label: "Comms",       icon: Megaphone },
  { id: "system",     label: "System",      icon: Activity },
  { id: "settings",   label: "Settings",    icon: Cog },
];

export const TABS = (() => {
  let tabs = [...BASE_TABS];
  if (SHOW_DEMO_TOOLS) {
    tabs = [...tabs, { id: "demo", label: "Demo Env", icon: Presentation }];
  }
  if (SHOW_LAUNCH_TOOLS) {
    tabs = [...tabs, { id: "provisioning", label: "Provisioning", icon: Server }, { id: "launch", label: "Launch", icon: Rocket }];
  }
  return tabs;
})();

export default TABS;
